import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Wallet,
  TrendUp,
  TrendDown,
  ChartLineUp,
  ArrowsClockwise,
  Trash,
  X,
  Lightning,
  CurrencyCircleDollar,
  Target
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const formatPrice = (price) => {
  if (price == null) return '—';
  if (price >= 1000) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
};

const StatCard = ({ icon: Icon, label, value, sub, color, iconBg }) => (
  <Card className="border-slate-200 rounded-sm shadow-none" data-testid={`paper-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-sm ${iconBg || 'bg-slate-100'}`}>
          <Icon size={20} className="text-slate-600" weight="regular" />
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-body">{label}</p>
          <p className={`font-data text-xl font-bold tabular-nums ${color || 'text-slate-900'}`}>{value}</p>
          {sub && <p className="text-xs text-slate-500 font-body">{sub}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const EquityCurve = ({ data, startingBalance }) => {
  if (!data || data.length < 2) return null;
  const chartData = data.map((d, i) => ({ index: i, value: d.value, date: d.date?.split('T')[0] }));
  return (
    <div className="h-48 w-full" data-testid="equity-curve">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <XAxis dataKey="date" hide />
          <YAxis domain={['auto', 'auto']} hide />
          <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '2px', fontSize: '12px', fontFamily: 'JetBrains Mono' }}
            formatter={(value) => [`$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'Valeur']} />
          <ReferenceLine y={startingBalance} stroke="#94a3b8" strokeDasharray="3 3" />
          <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function PaperTrading() {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [tab, setTab] = useState('positions');

  const fetchPortfolio = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const response = await fetch(`${API}/paper/portfolio`);
      const data = await response.json();
      setPortfolio(data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erreur lors du chargement du portefeuille");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchPortfolio(); }, []);

  // Auto-refresh portfolio with live prices every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchPortfolio(), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleCloseTrade = async () => {
    if (!selectedTrade) return;
    setClosing(true);
    try {
      const response = await fetch(`${API}/paper/close/${selectedTrade.id}`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Erreur");
      toast.success(`${data.message} | P&L: ${data.pnl >= 0 ? '+' : ''}$${data.pnl.toFixed(2)}`);
      setCloseDialogOpen(false);
      fetchPortfolio();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setClosing(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await fetch(`${API}/paper/reset`, { method: 'POST' });
      toast.success("Portefeuille reinitialise a $100,000");
      setResetDialogOpen(false);
      fetchPortfolio();
    } catch (error) {
      toast.error("Erreur lors de la reinitialisation");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="paper-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  const pnlColor = (portfolio?.total_pnl || 0) >= 0 ? 'text-emerald-600' : 'text-red-600';
  const openPositions = portfolio?.open_positions || [];
  const closedTrades = portfolio?.closed_trades || [];

  return (
    <div data-testid="paper-trading-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold text-slate-900">Paper Trading</h1>
            <Badge className="bg-amber-100 text-amber-700 border-0">Virtuel</Badge>
          </div>
          <p className="text-sm text-slate-500 font-body mt-1">
            Portefeuille de simulation - $100,000 de depart
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchPortfolio(true)} disabled={refreshing} className="rounded-sm" data-testid="refresh-paper-btn">
            <ArrowsClockwise size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/')} className="rounded-sm" data-testid="trade-from-dash-btn">
            <Lightning size={14} className="mr-1.5" />
            Trouver une Opportunite
          </Button>
          <Button variant="outline" size="sm" onClick={() => setResetDialogOpen(true)} className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="reset-paper-btn">
            <Trash size={14} className="mr-1.5" />
            Reinitialiser
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="paper-stats">
        <StatCard icon={Wallet} label="Valeur Totale" 
          value={`$${(portfolio?.total_value || 100000).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          sub={`Cash: $${(portfolio?.cash_balance || 100000).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          iconBg="bg-blue-50" />
        <StatCard icon={portfolio?.total_pnl >= 0 ? TrendUp : TrendDown} label="P&L Total"
          value={`${(portfolio?.total_pnl || 0) >= 0 ? '+' : ''}$${(portfolio?.total_pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          sub={`${(portfolio?.total_pnl_percent || 0) >= 0 ? '+' : ''}${(portfolio?.total_pnl_percent || 0).toFixed(2)}%`}
          color={pnlColor}
          iconBg={(portfolio?.total_pnl || 0) >= 0 ? 'bg-emerald-50' : 'bg-red-50'} />
        <StatCard icon={ChartLineUp} label="Win Rate"
          value={`${portfolio?.win_rate || 0}%`}
          sub={`${portfolio?.winning_trades || 0}W / ${portfolio?.losing_trades || 0}L`}
          iconBg="bg-amber-50" />
        <StatCard icon={Target} label="Positions"
          value={openPositions.length}
          sub={`$${(portfolio?.invested || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} investi`}
          iconBg="bg-purple-50" />
      </div>

      {/* Equity Curve */}
      {portfolio?.equity_curve?.length > 1 && (
        <Card className="border-slate-200 rounded-sm shadow-none mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">Courbe d'Equite</CardTitle>
          </CardHeader>
          <CardContent>
            <EquityCurve data={portfolio.equity_curve} startingBalance={100000} />
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        {[
          { key: 'positions', label: `Positions Ouvertes (${openPositions.length})` },
          { key: 'history', label: `Historique (${closedTrades.length})` }
        ].map(t => (
          <Button key={t.key} variant={tab === t.key ? 'default' : 'outline'} size="sm" className="rounded-sm text-xs" onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
            {t.label}
          </Button>
        ))}
      </div>

      {/* Positions Table */}
      <Card className="border-slate-200 rounded-sm shadow-none" data-testid="paper-table-card">
        <CardContent className="p-0">
          {tab === 'positions' && (
            openPositions.length === 0 ? (
              <div className="text-center py-12">
                <CurrencyCircleDollar size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 font-body">Aucune position ouverte</p>
                <p className="text-sm text-slate-400 font-body mt-1">
                  Allez sur le Dashboard pour ouvrir un paper trade
                </p>
                <Button variant="outline" size="sm" className="mt-4 rounded-sm" onClick={() => navigate('/')}>
                  Trouver une Opportunite
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Symbole</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Entree</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Prix Actuel</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Quantite</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Valeur</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">P&L</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Score</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {openPositions.map(pos => {
                      const pnl = pos.profit_loss || 0;
                      const pnlPct = pos.profit_loss_percent || 0;
                      const color = pnl > 0 ? 'text-emerald-600' : pnl < 0 ? 'text-red-600' : 'text-slate-500';
                      return (
                        <TableRow key={pos.id} data-testid={`paper-position-${pos.id}`}>
                          <TableCell>
                            <span className="font-heading font-bold text-slate-900 cursor-pointer hover:text-blue-600" onClick={() => navigate(`/opportunity/${pos.symbol}`)}>
                              {pos.symbol}
                            </span>
                          </TableCell>
                          <TableCell className="font-data tabular-nums text-slate-700">{formatPrice(pos.entry_price)}</TableCell>
                          <TableCell className="font-data tabular-nums text-blue-600">{formatPrice(pos.current_price)}</TableCell>
                          <TableCell className="font-data tabular-nums text-slate-700">{pos.quantity?.toFixed(6)}</TableCell>
                          <TableCell className="font-data tabular-nums text-slate-700">${pos.position_value?.toLocaleString()}</TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className={`font-data tabular-nums font-medium ${color}`}>
                                {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                              </span>
                              <span className={`font-data tabular-nums text-xs ${color}`}>
                                {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="font-data text-xs">{pos.score_at_entry}</Badge></TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedTrade(pos); setCloseDialogOpen(true); }}
                              className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2" data-testid={`close-paper-${pos.id}`}>
                              <X size={14} className="mr-1" /><span className="text-xs">Vendre</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )
          )}

          {tab === 'history' && (
            closedTrades.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-500 font-body">Aucun trade ferme</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Symbole</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Entree</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Sortie</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Valeur</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">P&L</TableHead>
                      <TableHead className="font-body text-[10px] uppercase tracking-wider">Resultat</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {closedTrades.map(trade => {
                      const pnl = trade.profit_loss || 0;
                      const pnlPct = trade.profit_loss_percent || 0;
                      const color = pnl > 0 ? 'text-emerald-600' : pnl < 0 ? 'text-red-600' : 'text-slate-500';
                      return (
                        <TableRow key={trade.id}>
                          <TableCell className="font-heading font-bold text-slate-900">{trade.symbol}</TableCell>
                          <TableCell className="font-data tabular-nums">{formatPrice(trade.entry_price)}</TableCell>
                          <TableCell className="font-data tabular-nums">{formatPrice(trade.exit_price)}</TableCell>
                          <TableCell className="font-data tabular-nums">${trade.position_value?.toLocaleString()}</TableCell>
                          <TableCell>
                            <span className={`font-data tabular-nums font-medium ${color}`}>
                              {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={pnl > 0 ? 'bg-emerald-100 text-emerald-700' : pnl < 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}>
                              {pnl > 0 ? 'Gagnant' : pnl < 0 ? 'Perdant' : 'Neutre'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Close Trade Dialog */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Vendre la Position</DialogTitle>
            <DialogDescription className="font-body">
              Fermer la position {selectedTrade?.symbol} au prix du marche
            </DialogDescription>
          </DialogHeader>
          {selectedTrade && (
            <div className="space-y-3 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-slate-500">Prix d'entree</p><p className="font-data font-medium">{formatPrice(selectedTrade.entry_price)}</p></div>
                <div><p className="text-slate-500">Prix actuel</p><p className="font-data font-medium text-blue-600">{formatPrice(selectedTrade.current_price)}</p></div>
                <div><p className="text-slate-500">Valeur investie</p><p className="font-data font-medium">${selectedTrade.position_value?.toLocaleString()}</p></div>
                <div>
                  <p className="text-slate-500">P&L estime</p>
                  <p className={`font-data font-medium ${(selectedTrade.profit_loss || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {(selectedTrade.profit_loss || 0) >= 0 ? '+' : ''}${(selectedTrade.profit_loss || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialogOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleCloseTrade} disabled={closing} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-close-paper-btn">
              {closing ? "Vente..." : "Confirmer la Vente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Reinitialiser le Portefeuille</DialogTitle>
            <DialogDescription className="font-body">
              Toutes les positions et l'historique seront supprimes. Le portefeuille sera reinitialise a $100,000.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleReset} disabled={resetting} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-reset-btn">
              {resetting ? "Reinitialisation..." : "Confirmer la Reinitialisation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
